import {Card, Box, Grid, TableContainer, Table, TableCell, TableRow, TableBody, TableHead} from '@material-ui/core';
import {useEffect, useState} from "react";
import {useTranslate} from "react-admin";

import {dashboard} from '../../styles/dashboard';
import {
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import axiosForApi from "../../config/axios-for-api";
import CircularProgress from "@material-ui/core/CircularProgress";
import StatHeader from "./StatHeader";

interface StatDataRow {
    month: number;
    month_fmt?: string;
    total: number;
    total_fmt: string;
    sap_total:string;
    shop_total:string;
    growth: number;
    growth_fmt: string;
    trend: number;
    trend_only?: boolean;
}


function toMonthName(monthNumber: number) {
    const date = new Date();
    date.setMonth(monthNumber - 1);

    return date.toLocaleString('de-DE', {
        month: 'short',
    });
}

const Loading = ({classes}: any) => {
    return (
        <Card className={classes.card}>
            <Box p={2} display={'flex'} justifyContent={'center'} alignItems={'center'} height={200}>
                <CircularProgress/>
            </Box>
        </Card>
    );
}

const GrowthFmt = ({growth, growth_fmt}: any) => {
    let color = '#4a8431';
    if (growth < 0) color = '#cc0000';

    const style = {color};
    return (<span style={style}>{growth_fmt}</span>);
}

const CustomTooltip = (props: any) => {

    const {payload, separator, itemStyle, label, labelStyle} = props;

    const listStyle = {padding: 0, margin: 0};

    const items = payload.map((entry: any, i: number) => {
        if (entry.type === 'none') {
            return null;
        }

        const finalItemStyle = {
            display: 'block',
            paddingTop: 4,
            paddingBottom: 4,
            color: entry.color || '#000',
            ...itemStyle,
        };

        let name = '';
        let value = '';

        if (entry.dataKey === 'total') {
            name = 'Total';
            value = entry.payload.total_fmt;
        }
        else if (entry.dataKey === 'growth') {
            name = 'Veränderung';
            value = entry.payload.growth_fmt;
        }
        else if (entry.dataKey === 'sap_total') {
            name = 'SAP';
            value = entry.payload.sap_total;
        }
        else if (entry.dataKey === 'shop_total') {
            name = 'Shop';
            value = entry.payload.shop_total;
        }

        return (
            // eslint-disable-next-line react/no-array-index-key
            <li className="recharts-tooltip-item" key={`tooltip-item-${i}`} style={finalItemStyle}>
                <span className="recharts-tooltip-item-name">{name}</span>
                <span className="recharts-tooltip-item-separator">{separator}</span>
                <span className="recharts-tooltip-item-value">{value}</span>
                <span className="recharts-tooltip-item-unit">{entry.unit || ''}</span>
            </li>
        );
    });

    return (
        <div className='recharts-default-tooltip' style={{
            margin: 0,
            padding: 10,
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            whiteSpace: 'nowrap'
        }}>
            <p className={'recharts-tooltip-label'} style={{margin:0, ...labelStyle}}>
                {label}
            </p>
            <ul className="recharts-tooltip-item-list" style={listStyle}>
                {items}
            </ul>
        </div>

    );
}

const NewRegisteredCustomersThisYear = ({countries, forCatalogOffers = false}: { countries: string[] | null, forCatalogOffers?:boolean }) => {
    const classes = dashboard();
    const translate = useTranslate();

    const [shouldDrawChart, setShouldDrawChart] = useState(false);
    const [shouldLoadStatsData, setShouldLoadStatsData] = useState(false);

    const [tableData, setTableData] = useState<StatDataRow[]>([]);
    const [chartData, setChartData] = useState<StatDataRow[]>([]);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        const loadStatsData = async () => {
            const {data} = await axiosForApi.get(`api/admin/${forCatalogOffers?'catalog-offer-stats':'stats'}/new-registered-${forCatalogOffers?'catalog-customers':'customers'}-this-year`, {
                params: {
                    countries
                }
            });

            if(!cancel) {

                setLoading(false);
                setShouldLoadStatsData(false);
                setShouldDrawChart(true);

                const newTableData = [...data ?? []];
                setTableData(newTableData.filter(el => !el.trend_only).sort((a: StatDataRow, b: StatDataRow) => b.month - a.month));

                const newChartData = [...data ?? []];
                setChartData(newChartData.map((el: StatDataRow) => {
                    el.month_fmt = toMonthName(el.month);
                    return el;
                }));
            }
        }

        shouldLoadStatsData && loadStatsData();

        return () => {
            cancel = true;
        }
    }, [shouldLoadStatsData, countries, forCatalogOffers]);

    useEffect(() => {
        setLoading(true);
        setShouldLoadStatsData(true);
    }, [countries]);


    if (loading) return (<Loading classes={classes}/>);

    return (
        <Card className={classes.card}>
            <Box p={2}>
                <StatHeader
                    justifyContent='center'
                    title={translate('resources.admin/dashboard.headers.new-registered-customers-this-year')}
                    help={`resources.admin/dashboard.${forCatalogOffers?'catalog_offers_help':'help'}.new-registered-${forCatalogOffers?'catalog-customers':'customers'}-this-year`}
                />
                <Grid container spacing={0}>
                    <Grid item xs={12} sm={4}>
                            <TableContainer className={'scrollable'} style={{paddingRight:'1rem', height: '300px', overflowY: 'scroll'}}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{position:'sticky', top:0, backgroundColor:'#fff'}}></TableCell>
                                            <TableCell align="right" style={{position:'sticky', top:0, backgroundColor:'#fff'}}>Sap</TableCell>
                                            <TableCell align="right" style={{position:'sticky', top:0, backgroundColor:'#fff'}}>Shop</TableCell>
                                            <TableCell align="right" style={{position:'sticky', top:0, backgroundColor:'#fff'}}>Total</TableCell>
                                            <TableCell align="right" style={{position:'sticky', top:0, backgroundColor:'#fff'}}>Veränderung Vormonat</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableData.map((data, i) => (
                                            <TableRow key={`row_${i + 1}`}>
                                                <TableCell padding={'none'}>
                                                    {toMonthName(data.month)}
                                                </TableCell>
                                                <TableCell align="right" style={{...(+data.sap_total>0?{background:'#8c8caf', color:'#fff'}:{})}}>
                                                    {data.sap_total}
                                                </TableCell>
                                                <TableCell align="right" style={{...(+data.shop_total>0?{background:'#c00', color:'#fff'}:{})}}>
                                                    {data.shop_total}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {data.total_fmt}
                                                </TableCell>
                                                <TableCell padding={'none'} align="right">
                                                    <GrowthFmt {...data} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <div style={{width: '100%', 'height': '300px'}}>
                            {shouldDrawChart && (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart
                                        width={500}
                                        height={400}
                                        data={chartData}
                                        margin={{
                                            top: 0,
                                            right: 0,
                                            bottom: 0,
                                            left: 0,
                                        }}
                                    >
                                        <CartesianGrid stroke="#f5f5f5"/>
                                        <XAxis dataKey="month_fmt" interval={0} angle={-30}/>
                                        <YAxis yAxisId="left" width={80} tickFormatter={(v:number) => v.toLocaleString('de-DE')}/>
                                        <YAxis yAxisId="right" orientation="right" tickFormatter={(v:number) => v.toLocaleString('de-DE')}/>
                                        <Tooltip content={<CustomTooltip/>}/>
                                        {/*<Legend/>*/}
                                        <Bar yAxisId="left" dataKey="total" barSize={20} fill="#333"/>
                                        <Bar yAxisId="left" dataKey="sap_total" barSize={5} fill="#8c8caf"/>
                                        <Bar yAxisId="left" dataKey="shop_total" barSize={5} fill="#c00"/>
                                        <Line yAxisId="right" type="monotone" dataKey="growth" stroke="#ff7300"/>
                                    </ComposedChart>
                                </ResponsiveContainer>)}
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );
}

export default NewRegisteredCustomersThisYear;
